import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

const PANEL_SELECTOR = '.mat-mdc-select-panel';
const OPTION_SELECTOR = '.mat-mdc-option';
const SELECT_SELECTOR = 'mat-select, .mat-mdc-select';
/** Panels open on the next frame and animate in; look again shortly after. */
const SCAN_DELAYS_MS = [0, 50, 250];

export function optionMatchesSearch(optionText: string, query: string): boolean {
  return optionText.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

/**
 * Adds a search box to every open mat-select panel. Driven by the user opening a
 * select (click / keyboard on a mat-select), not by a document-wide
 * MutationObserver: that ran on every AG Grid row and cell render. Panels render
 * as CDK popovers inside the mat-select itself (CDK 21), so the search is
 * document-wide but only runs right after a select interaction.
 */
@Injectable({ providedIn: 'root' })
export class SelectLiveSearchService implements OnDestroy {
  private started = false;
  private readonly panelObservers = new WeakMap<HTMLElement, MutationObserver>();
  private readonly onInteraction = (event: Event) => {
    const target = event.target as Element | null;
    if (!target?.closest?.(SELECT_SELECTOR)) return;
    for (const delay of SCAN_DELAYS_MS) setTimeout(() => this.scanOpenPanels(), delay);
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly zone: NgZone,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => {
      for (const type of ['click', 'keydown']) {
        this.document.addEventListener(type, this.onInteraction, true);
      }
    });
  }

  scanOpenPanels(): void {
    this.document.querySelectorAll<HTMLElement>(PANEL_SELECTOR).forEach(panel => this.enhancePanel(panel));
  }

  enhancePanel(panel: HTMLElement): void {
    let search = panel.querySelector<HTMLInputElement>('.df-select-live-search__input');
    if (!search) {
      const wrapper = this.document.createElement('div');
      wrapper.className = 'df-select-live-search';
      wrapper.setAttribute('role', 'search');

      search = this.document.createElement('input');
      search.className = 'df-select-live-search__input';
      search.type = 'search';
      search.autocomplete = 'off';
      search.placeholder = 'Search / 搜索';
      search.setAttribute('aria-label', 'Filter dropdown options');

      const empty = this.document.createElement('div');
      empty.className = 'df-select-live-search__empty';
      empty.textContent = 'No matching options / 无匹配选项';
      empty.hidden = true;

      wrapper.append(search, empty);
      panel.prepend(wrapper);

      search.addEventListener('input', () => this.filterPanel(panel, search!.value));
      for (const eventName of ['keydown', 'keyup', 'mousedown', 'click']) {
        search.addEventListener(eventName, event => event.stopPropagation());
      }
      queueMicrotask(() => search?.focus());

      // Options can arrive after the panel opens; re-filter within this panel only.
      if (typeof MutationObserver !== 'undefined' && !this.panelObservers.has(panel)) {
        const observer = new MutationObserver(() => {
          if (!panel.isConnected) return observer.disconnect();
          this.filterPanel(panel, search!.value);
        });
        observer.observe(panel, { childList: true, subtree: true });
        this.panelObservers.set(panel, observer);
      }
    }

    this.filterPanel(panel, search.value);
  }

  private filterPanel(panel: HTMLElement, query: string): void {
    const options = Array.from(panel.querySelectorAll<HTMLElement>(OPTION_SELECTOR));
    let visibleCount = 0;
    for (const option of options) {
      const visible = optionMatchesSearch(option.textContent ?? '', query);
      // Material renders options with `display: flex`, which can override the
      // browser's [hidden] rule. An inline display value wins consistently.
      option.style.display = visible ? '' : 'none';
      if (visible) visibleCount += 1;
    }
    const empty = panel.querySelector<HTMLElement>('.df-select-live-search__empty');
    if (empty) empty.style.display = visibleCount > 0 ? 'none' : '';
  }

  ngOnDestroy(): void {
    for (const type of ['click', 'keydown']) {
      this.document.removeEventListener(type, this.onInteraction, true);
    }
    this.started = false;
  }
}
